/**
 * P3-38: AccelerateTimeBtn —— 切换时间加速。
 *
 * 对齐 `js/time.js:1071-1110`：
 * - model.toggle 作为单个 link 渲染
 * - 点击 toggle `game.time.isAccelerated`
 */
import { useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import { useButtonModel, shallowSnapEq, type ModernButtonController } from "../bonfire/useButtonModel";
import { makeModernTooltip } from "../bonfire/useButtonModel";

interface AccelerateTimeSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  toggleTitle: string;
  toggleTooltip: string;
  toggleCssClass: string;
}

function toSnapshot(model: Record<string, unknown>): AccelerateTimeSnapshot {
  const toggle = (model.toggle as Record<string, unknown>) ?? {};
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: Boolean(model.visible),
    enabled: Boolean(model.enabled),
    toggleTitle: String(toggle.title ?? ""),
    toggleTooltip: String(toggle.tooltip ?? ""),
    toggleCssClass: String(toggle.cssClass ?? ""),
  };
}

export default function AccelerateTimeBtn() {
  const controller = useMemo<ModernButtonController | null>(() => {
    const ControllerClass = (
      window as unknown as {
        classes?: { ui?: { time?: { AccelerateTimeBtnController?: new (game: GamePage) => ModernButtonController } } };
      }
    ).classes?.ui?.time?.AccelerateTimeBtnController;
    const game = getGame();
    if (typeof ControllerClass !== "function" || !game) {
      return null;
    }
    return new ControllerClass(game);
  }, []);

  const { snap, modelRef } = useButtonModel(
    controller,
    () => ({
      name: (window as unknown as { $I?: (key: string) => string }).$I?.("time.AccelerateTimeBtn.label") ?? "",
      description: (window as unknown as { $I?: (key: string) => string }).$I?.("time.AccelerateTimeBtn.desc") ?? "",
      prices: [],
    }),
    toSnapshot,
    shallowSnapEq,
  );

  const getTooltip = makeModernTooltip(
    () => controller,
    () => ({
      name: (window as unknown as { $I?: (key: string) => string }).$I?.("time.AccelerateTimeBtn.label") ?? "",
      description: (window as unknown as { $I?: (key: string) => string }).$I?.("time.AccelerateTimeBtn.desc") ?? "",
      prices: [],
    }),
  );

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      getTooltip={getTooltip}
      onClick={() => {
        controller?.buyItem?.(modelRef.current, null);
        getGame()?.render();
      }}
      links={[
        {
          title: snap.toggleTitle,
          cssClass: snap.toggleCssClass,
          tooltip: snap.toggleTooltip,
          handler: () => {
            controller?.buyItem?.(modelRef.current, null);
            getGame()?.render();
          },
        },
      ]}
    />
  );
}
